import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

/**
 * OCR結果画面で「いまフォーカスしている項目」を配る Context（#3275）。
 *
 * この値は画像上の座標枠をハイライトするためだけに使われる。
 * PatternAiOcrResult の useState に置くと、入力欄をクリックするたびに
 * 7,000行の親コンポーネント全体が再レンダーされ、明細の全セル
 * （MUI の DatePicker を含む）が再評価される。本番実測では
 * クリック1回で Layerize に3.3秒かかっていた。
 *
 * 値を読む側（ImageOverlay）と書く側（明細セル）を別の Context に分け、
 * 書く側は参照が変わらない関数だけを受け取るようにしている。
 * これにより setFocusItem を呼んでも、値を読んでいる側だけが再レンダーされ、
 * 明細行は再評価されない。
 */
const FocusItemValueContext = createContext<string>('');

/** setter は参照が変わらないため、購読しても再レンダーされない */
const FocusItemSetterContext = createContext<(value: string) => void>(() => undefined);

export const FocusItemProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [focusItem, setFocusItemState] = useState<string>('');
  // 同じ値での更新を弾き、無駄な再レンダーを起こさない
  const latestRef = useRef<string>('');

  const setFocusItem = useCallback((value: string): void => {
    if (latestRef.current === value) return;
    latestRef.current = value;
    setFocusItemState(value);
  }, []);

  return (
    <FocusItemSetterContext.Provider value={setFocusItem}>
      <FocusItemValueContext.Provider value={focusItem}>{children}</FocusItemValueContext.Provider>
    </FocusItemSetterContext.Provider>
  );
};

/** フォーカス中の項目キーを読む。値が変わると購読側が再レンダーされる */
export const useFocusItem = (): string => useContext(FocusItemValueContext);

/** フォーカスを設定する。参照が固定なので購読しても再レンダーされない */
export const useSetFocusItem = (): ((value: string) => void) => useContext(FocusItemSetterContext);
